import { useCloseRequest } from 'src/modules/shared/hooks/use-close-request';
import { CloseRequestContext } from 'src/modules/shared/contexts/close-request-context';
import React, { useState, useEffect, useMemo } from 'react';
import { Menu, MenuHandler, MenuList } from '@material-tailwind/react';
import type { ComponentSize } from 'src/modules/shared/types/component.type';
import {
  getMenuItemClasses,
  getMenuItemContentClasses,
  getMenuItemContentColor,
  getMenuItemContentWeight,
  menuListClasses,
  separatorClasses,
} from 'src/modules/shared/styles/dropdown.styles';
import { Text } from 'src/modules/shared/components/atoms/text';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { useMediaQuery } from 'src/modules/shared/hooks/use-media-query';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

export interface DropdownItem {
  onClick: () => void;
  separator?: boolean;
  disabled?: boolean;
  closeOnClick?: boolean;
  content: React.ReactNode | string;
  icon?: React.ReactNode;
  selected?: boolean;
}

export type DropdownPlacement =
  | 'top-start'
  | 'top'
  | 'top-end'
  | 'right-start'
  | 'right'
  | 'right-end'
  | 'bottom-start'
  | 'bottom'
  | 'bottom-end'
  | 'left-start'
  | 'left'
  | 'left-end';

export interface DropdownProps {
  items: DropdownItem[];
  handler: React.ReactNode;
  className?: string;
  menuClassName?: string;
  size?: ComponentSize;
  placement?: DropdownPlacement;
  isLoading?: boolean;
  disabled?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  mobileTitle?: React.ReactNode;
}

const MOBILE_DROPDOWN_BREAKPOINT_PX = 640;

const Dropdown: React.FC<DropdownProps> = ({
  items,
  handler,
  className = '',
  menuClassName = '',
  size = 'md',
  placement = 'bottom-end',
  isLoading = false,
  disabled = false,
  onOpenChange,
  mobileTitle,
}) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const useMobilePresentation = useMediaQuery(
    `(max-width: ${MOBILE_DROPDOWN_BREAKPOINT_PX - 1}px)`
  );

  const handlerClasses = `inline-flex max-w-full items-center gap-2 ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`;

  const handleOpen = (open: boolean) => {
    if (isLoading || disabled) return;

    setIsOpen(open);
  };

  const closeLayerId = useCloseRequest({
    isOpen: isOpen && !useMobilePresentation,
    onClose: () => handleOpen(false),
  });

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  const renderedItems = useMemo(() => {
    return items.map((item, index) => {
      const isItemDisabled = (item.disabled ?? false) || isLoading || disabled;
      const shouldCloseOnClick = item.closeOnClick ?? true;

      return (
        <React.Fragment key={index}>
          {item.content !== null && (
            <button
              type="button"
              disabled={isItemDisabled}
              onClick={() => {
                if (item.selected) {
                  return;
                }

                if (shouldCloseOnClick) {
                  setIsOpen(false);
                }

                item.onClick();
              }}
              className={getMenuItemClasses(
                size,
                item.selected,
                false,
                isItemDisabled
              )}
            >
              {item.icon}
              {typeof item.content === 'string' ? (
                <Text
                  size="paragraph"
                  weight={getMenuItemContentWeight(item.selected)}
                  color={getMenuItemContentColor(item.selected)}
                  className={getMenuItemContentClasses()}
                >
                  {item.content}
                </Text>
              ) : (
                item.content
              )}
            </button>
          )}
          {item.separator && index < items.length - 1 && (
            <hr className={separatorClasses} />
          )}
        </React.Fragment>
      );
    });
  }, [items, isLoading, disabled, size]);

  if (useMobilePresentation) {
    return (
      <>
        <div
          className={handlerClasses}
          onClick={() => handleOpen(!isOpen)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleOpen(!isOpen);
            }
          }}
          role="button"
          tabIndex={disabled ? -1 : 0}
        >
          {handler}
        </div>
        <Modal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          header={
            <div className="min-w-0">
              {typeof mobileTitle === 'string' ? (
                <Text size="h6" weight="semibold" className="truncate">
                  {mobileTitle}
                </Text>
              ) : (
                mobileTitle ?? (
                  <Text size="h6" weight="semibold" className="truncate">
                    {t('shared.dropdown.mobileTitle')}
                  </Text>
                )
              )}
            </div>
          }
          dialogClassName="!h-[100dvh] !max-h-[100dvh] !max-w-full !rounded-none"
          bodyClassName="!p-0 !overflow-hidden"
          body={
            <div className="min-h-0 w-full overflow-y-auto overscroll-contain">
              <div className="flex flex-col gap-1 p-3">{renderedItems}</div>
            </div>
          }
        />
      </>
    );
  }

  return (
    <CloseRequestContext.Provider value={closeLayerId}>
      <Menu open={isOpen} handler={handleOpen} placement={placement}>
        <MenuHandler>
          <div className={handlerClasses}>{handler}</div>
        </MenuHandler>
        <MenuList className={`${menuListClasses} ${menuClassName}`}>
          {renderedItems}
        </MenuList>
      </Menu>
    </CloseRequestContext.Provider>
  );
};

export default Dropdown;
